// src/api/class/classQueries.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  createClass,
  getAllClasses,
  getClassById,
  updateClass,
  deleteClass
} from './classApi';

/**
 * Hook to fetch all classes
 */
export const useGetAllClasses = () => {
  return useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      try {
        const data = await getAllClasses();
        return data;
      } catch (error) {
        console.error('Error in useGetAllClasses:', error);
        toast.error('Failed to fetch classes');
        throw error;
      }
    },
  });
};

/**
 * Hook to fetch a class by its ID
 * @param {string} classId - The ID of the class to fetch
 */
export const useGetClassById = (classId) => {
  return useQuery({
    queryKey: ['class', classId],
    queryFn: () => getClassById(classId),
    enabled: !!classId,
    onError: (error) => {
      console.error('Error fetching class:', error);
      toast.error('Failed to fetch class details');
    }
  });
};

/**
 * Hook to create a new class
 */
export const useCreateClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createClass,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['classes']);
      toast.success('Class created successfully');
      return data;
    },
    onError: (error) => {
      console.error('Error creating class:', error);
      toast.error(error.response?.data?.message || 'Failed to create class');
    }
  });
};

/**
 * Hook to update a class
 */
export const useUpdateClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ classId, data }) => updateClass(classId, data),
    onSuccess: (_, { classId }) => {
      queryClient.invalidateQueries(['classes']);
      queryClient.invalidateQueries(['class', classId]);
      toast.success('Class updated successfully');
    },
    onError: (error) => {
      console.error('Error updating class:', error);
      toast.error(error.response?.data?.message || 'Failed to update class');
    }
  });
};

/**
 * Hook to delete a class
 */
export const useDeleteClass = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteClass,
    onSuccess: () => {
      queryClient.invalidateQueries(['classes']);
      toast.success('Class deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting class:', error);
      toast.error(error.response?.data?.message || 'Failed to delete class');
    }
  });
};